import {
  $applyNodeReplacement,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from 'lexical';

export const PRESENTATION_VARIABLE_CLASS = 'presentation-variable';

export class PresentationVariableNode extends TextNode {
  static override getType(): string {
    return 'presentation-variable';
  }

  static override clone(
    node: PresentationVariableNode
  ): PresentationVariableNode {
    // clone is an internal Lexical API for getWritable() — use a simple
    // shallow copy instead of $applyNodeReplacement (which is for $create factories).
    return new PresentationVariableNode(node.__text, node.__key);
  }

  constructor(text: string, key?: NodeKey) {
    super(text, key);
  }

  override createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.classList.add(PRESENTATION_VARIABLE_CLASS);
    dom.style.borderRadius = '4px';
    return dom;
  }

  override updateDOM(
    _prevNode: TextNode,
    dom: HTMLElement,
    _config: EditorConfig
  ): boolean {
    // Ensure styles persist across updates. We don't call super.updateDOM()
    // because this node is only used in read-only mode and doesn't need
    // format/style reconciliation from TextNode.
    dom.classList.add(PRESENTATION_VARIABLE_CLASS);
    dom.style.borderRadius = '4px';
    return false;
  }

  override exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-presentation-variable', 'true');
    element.textContent = this.__text;
    return { element };
  }

  static override importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-presentation-variable')) {
          return null;
        }
        return {
          conversion: $convertPresentationVariableElement,
          priority: 2,
        };
      },
    };
  }

  static override importJSON(
    serializedNode: SerializedTextNode
  ): PresentationVariableNode {
    return $createPresentationVariableNode(serializedNode.text);
  }

  override exportJSON(): SerializedTextNode {
    return {
      ...super.exportJSON(),
      type: PresentationVariableNode.getType(),
    };
  }
}

function $convertPresentationVariableElement(
  domNode: HTMLElement
): DOMConversionOutput | null {
  const text = domNode.textContent;
  if (!text) {
    return null;
  }
  return { node: $createPresentationVariableNode(text) };
}

export function $createPresentationVariableNode(
  text: string
): PresentationVariableNode {
  // Token mode makes the node atomic — typing at its boundary creates a new
  // TextNode instead of appending characters to the styled variable node.
  const node = new PresentationVariableNode(text);
  node.setMode('token');
  return $applyNodeReplacement(node);
}
